// URL search params for the evidence screens (constitution §5.6: filters live in the URL). Every
// value is checked here; anything else in the URL is dropped. Kept small: route files import it,
// so it lands in the initial bundle.
import type { SafeguardFilters, SafeguardStatus } from '@cis/api-client';
import { EVIDENCE_STATUSES, isIsoDate, today } from '@cis/shared';
import { parseFreezeParam } from '../freeze/use-freeze-param.ts';

const MAX_QUERY = 64; // the API's q limit for /safeguards

// Same list as the api-client's SAFEGUARD_STATUSES, built here so the route chunk stays small.
export const SAFEGUARD_FILTERS: readonly SafeguardStatus[] = ['none', ...EVIDENCE_STATUSES];

// A family is the CIS control number: matches "4", "16"; rejects "04x", "CIS 4", "123".
// https://regex101.com/?flavor=javascript&regex=%5E%5B1-9%5D%5Cd%3F%24&testString=4%0A16%0A04x%0A123
const FAMILY = /^[1-9]\d?$/;

// An evidence line code is the safeguard number: matches "4.1", "16.14"; rejects "CIS 4.1", "4".
// https://regex101.com/?flavor=javascript&regex=%5E%5Cd%7B1%2C2%7D%5C.%5Cd%7B1%2C2%7D%24&testString=4.1%0A16.14%0ACIS%204.1%0A4
const LINE_CODE = /^\d{1,2}\.\d{1,2}$/;

export const isLineCode = (value: string): boolean => LINE_CODE.test(value);

/** "CIS 4.1" → "4.1": the evidence line of a safeguard has the safeguard's number. */
export function lineCodeOf(safeguardCode: string): string | null {
  const number = safeguardCode.startsWith('CIS ') ? safeguardCode.slice('CIS '.length) : '';
  return isLineCode(number) ? number : null;
}

export interface SafeguardSearch {
  readonly family?: string | undefined;
  readonly status?: SafeguardStatus | undefined;
  readonly q?: string | undefined;
}

// The router parses "4" in the URL as a number; filters take it back as text.
const asText = (v: unknown): string =>
  typeof v === 'string' || typeof v === 'number' ? String(v).trim() : '';

/** Every key is returned, so an unchecked raw value cannot survive the router's merge. */
export function validateSafeguardSearch(search: Record<string, unknown>): SafeguardSearch {
  const family = asText(search['family']);
  const q = asText(search['q']);
  return {
    family: FAMILY.test(family) ? family : undefined,
    status: SAFEGUARD_FILTERS.find((s) => s === search['status']),
    q: q === '' || q.length > MAX_QUERY ? undefined : q,
  };
}

export const filtersOf = (s: SafeguardSearch): SafeguardFilters => ({
  family: s.family,
  status: s.status,
  q: s.q,
});

export interface LineSearch {
  readonly asOf?: string | undefined;
  readonly freeze?: number | undefined;
}

/**
 * `?asOf=` is a calendar date up to today in program time, `?freeze=` a freeze id. A frozen view
 * wins over a day in the past: both are read-only, and the freeze is the hard copy (spec J5).
 */
export function validateLineSearch(search: Record<string, unknown>): LineSearch {
  const freeze = parseFreezeParam(search['freeze']) ?? undefined;
  const raw = search['asOf'];
  const asOf = typeof raw === 'string' && isIsoDate(raw) && raw <= today() ? raw : undefined;
  return { asOf: freeze === undefined ? asOf : undefined, freeze };
}
